/**
 * Avante release notes shown on the "Novedades" page (ChangelogPage.tsx):
 * one entry per merged change, written for the people who use the app.
 * The text is Spanish-only data (Avante's working language); the page
 * chrome around it goes through i18n.
 *
 * `mergedAt` is the UTC merge time; the page groups entries by calendar day
 * in Europe/Madrid, newest first.
 */

export type ChangelogCategory = "new" | "mobile" | "desktop" | "fix";

export interface ChangelogEntry {
  /** Number of the change in the repository, shown as a small badge. */
  pr: number;
  /** UTC ISO timestamp. */
  mergedAt: string;
  title: string;
  description: string;
  categories: readonly ChangelogCategory[];
}

export const CHANGELOG_TIME_ZONE = "Europe/Madrid";

export const AVANTE_CHANGELOG: readonly ChangelogEntry[] = [
  {
    pr: 31,
    mergedAt: "2026-11-04T13:57:42Z",
    title: "Pestaña Mercado en el móvil",
    description:
      "La barra inferior suma una cuarta pestaña para ir directamente a Inteligencia de mercado.",
    categories: ["new", "mobile"],
  },
  {
    pr: 30,
    mergedAt: "2026-11-04T13:57:39Z",
    title: "Fechas en formato español",
    description:
      "Fechas y horas en formato de 24 horas según el idioma elegido, y textos que faltaban por traducir. La app arranca siempre en español.",
    categories: ["fix"],
  },
  {
    pr: 29,
    mergedAt: "2026-11-04T13:57:36Z",
    title: "Listas más claras mientras cargan",
    description:
      "Las listas muestran una animación de carga, las tablas se ven como tarjetas en el móvil y los nombres incompletos ya no muestran «null».",
    categories: ["mobile", "fix"],
  },
  {
    pr: 28,
    mergedAt: "2026-11-04T13:57:26Z",
    title: "Diálogos a pantalla completa y botón Atrás",
    description:
      "En el móvil los diálogos se abren como hojas a pantalla completa, los toques ya no atraviesan las capas al cerrarlas y todas las páginas tienen botón Atrás.",
    categories: ["mobile", "fix"],
  },
  {
    pr: 27,
    mergedAt: "2026-11-04T13:57:24Z",
    title: "Tableros, informes y calendario con estilo cristal",
    description:
      "Columnas de cristal en los tableros de oportunidades y tareas, filtros por etapa en Informes y un calendario mensual como el del iPhone.",
    categories: ["mobile"],
  },
  {
    pr: 26,
    mergedAt: "2026-11-04T11:20:29Z",
    title: "Inteligencia de mercado",
    description:
      "Nueva sección con el informe diario, el resumen semanal, las fichas de competidores y un tablero de ideas.",
    categories: ["new"],
  },
  {
    pr: 25,
    mergedAt: "2026-11-04T00:34:22Z",
    title: "Ventanas a pantalla completa desde la barra superior",
    description:
      "Ajustes, notificaciones y agenda se abren desde su propio botón con una animación de cristal, y se cierran con la X, deslizando hacia abajo o con Atrás.",
    categories: ["new", "mobile"],
  },
  {
    pr: 24,
    mergedAt: "2026-11-03T23:02:14Z",
    title: "Cambiar el idioma ya no cierra los ajustes",
    description:
      "Al elegir otro idioma en los ajustes del móvil, la hoja sigue abierta y se ve correctamente.",
    categories: ["mobile", "fix"],
  },
  {
    pr: 23,
    mergedAt: "2026-11-03T22:30:36Z",
    title: "Sin franjas bajo menús y diálogos en el iPhone",
    description:
      "El menú +, las hojas y los diálogos cubren toda la pantalla de la app instalada, y la barra de pestañas es más compacta.",
    categories: ["mobile", "fix"],
  },
  {
    pr: 22,
    mergedAt: "2026-11-03T19:21:32Z",
    title: "Invitaciones y recuperación de contraseña",
    description:
      "Los enlaces de invitación y de «¿Has olvidado la contraseña?» llevan directamente a crear la contraseña, sin cerrar la sesión.",
    categories: ["fix"],
  },
  {
    pr: 21,
    mergedAt: "2026-11-03T17:13:24Z",
    title: "Sin destello blanco al abrir",
    description:
      "La app instalada en el iPhone arranca directamente en azul marino, sin el fogonazo blanco.",
    categories: ["mobile", "fix"],
  },
  {
    pr: 20,
    mergedAt: "2026-11-03T16:24:17Z",
    title: "Bienvenida más pausada",
    description:
      "La pantalla de bienvenida dura un poco más y la barra inferior aparece cuando termina.",
    categories: ["mobile"],
  },
  {
    pr: 19,
    mergedAt: "2026-11-03T16:23:55Z",
    title: "Barra de pestañas a la altura correcta",
    description:
      "En la app instalada del iPhone, la barra inferior queda cerca del borde, como en las apps del sistema.",
    categories: ["mobile", "fix"],
  },
  {
    pr: 18,
    mergedAt: "2026-11-03T02:41:56Z",
    title: "Nueva búsqueda",
    description:
      "En el móvil la búsqueda ocupa toda la pantalla con los resultados agrupados; en el ordenador se abre con Ctrl+K. Las oportunidades encontradas abren su ficha.",
    categories: ["new", "mobile", "desktop"],
  },
  {
    pr: 17,
    mergedAt: "2026-11-03T02:36:29Z",
    title: "La X de los ajustes vuelve a su sitio",
    description:
      "Al abrir los ajustes en el móvil, el botón de cerrar aparece justo sobre tu avatar.",
    categories: ["mobile", "fix"],
  },
  {
    pr: 16,
    mergedAt: "2026-11-03T02:25:42Z",
    title: "Inicio con vistas «Hoy» y «Pulso»",
    description:
      "En el ordenador, el inicio tiene dos vistas: Hoy, con lo que necesita atención, y Pulso, con el estado del negocio. Puedes elegir la vista por defecto en Ajustes.",
    categories: ["new", "desktop"],
  },
  {
    pr: 15,
    mergedAt: "2026-11-03T02:16:10Z",
    title: "Selector de pestañas más suave",
    description:
      "La marca de la pestaña activa es más discreta, más estrecha y rebota menos.",
    categories: ["mobile"],
  },
  {
    pr: 14,
    mergedAt: "2026-11-03T01:55:20Z",
    title: "Bienvenida a pantalla completa en el iPhone",
    description:
      "La pantalla de bienvenida y la barra inferior llegan hasta el borde de la pantalla en la app instalada.",
    categories: ["mobile", "fix"],
  },
  {
    pr: 13,
    mergedAt: "2026-11-03T01:14:51Z",
    title: "Barra inferior con Inicio, Tareas y Oportunidades",
    description:
      "La pestaña activa se marca con una cápsula de cristal que se desliza. Informes y Mensajes siguen a mano en el menú + y en la pantalla Más.",
    categories: ["mobile"],
  },
  {
    pr: 12,
    mergedAt: "2026-11-03T01:01:29Z",
    title: "Hoja de ajustes más pulida",
    description:
      "La hoja de ajustes del móvil se coloca bajo la isla del iPhone, con esquinas redondeadas, difuminado al desplazarte y cierre deslizando hacia abajo.",
    categories: ["mobile"],
  },
  {
    pr: 10,
    mergedAt: "2026-11-03T01:01:22Z",
    title: "Pantalla de bienvenida",
    description:
      "Al abrir Avante aparece una pantalla azul marino con el logotipo mientras carga y, después, un saludo con tu nombre.",
    categories: ["new", "mobile"],
  },
  {
    pr: 11,
    mergedAt: "2026-11-03T01:01:14Z",
    title: "Tareas internas y de cliente",
    description:
      "Una tarea puede ser interna del equipo, con un responsable y sin contacto, o de un proyecto o cliente. Se distinguen con una etiqueta y un filtro.",
    categories: ["new"],
  },
  {
    pr: 9,
    mergedAt: "2026-11-03T00:05:11Z",
    title: "El menú + nace de la barra de pestañas",
    description:
      "Al tocar +, el panel crece desde la barra inferior con una animación como la de iOS. Se cierra tocando fuera, deslizando hacia abajo o con Escape.",
    categories: ["mobile"],
  },
  {
    pr: 8,
    mergedAt: "2026-11-02T22:34:45Z",
    title: "Pantalla «Más» y empresas editables en el móvil",
    description:
      "Ya puedes editar empresas desde el móvil, el menú + tiene accesos rápidos y los módulos se reúnen en la pantalla Más.",
    categories: ["new", "mobile"],
  },
  {
    pr: 7,
    mergedAt: "2026-11-02T19:51:24Z",
    title: "Escritorio rediseñado",
    description:
      "Barra lateral plegable con grupos, objetivo del mes y modo oscuro, y un inicio con datos reales de contactos, oportunidades y tareas.",
    categories: ["new", "desktop"],
  },
  {
    pr: 6,
    mergedAt: "2026-11-02T17:55:44Z",
    title: "Agenda por día, semana y mes",
    description:
      "Barras más finas, agenda con vistas Día, Semana y Mes, formularios que ya no se bloquean con el teclado e iconos de la app renovados.",
    categories: ["new", "mobile"],
  },
  {
    pr: 5,
    mergedAt: "2026-11-02T14:26:58Z",
    title: "Estilo cristal y ajustes como en el iPhone",
    description:
      "Barras de cristal translúcido, nueva hoja de Cuenta y ajustes al estilo de iOS y app a pantalla completa, sin la franja negra superior.",
    categories: ["mobile"],
  },
  {
    pr: 4,
    mergedAt: "2026-11-02T11:58:36Z",
    title: "Nuevo logotipo de la vela",
    description:
      "El logotipo pasa a ser una vela, todas las pantallas del móvil tienen avatar y buscador arriba y el menú + se abre con un movimiento más natural.",
    categories: ["mobile"],
  },
  {
    pr: 3,
    mergedAt: "2026-11-01T23:54:51Z",
    title: "Inicio, agenda y barra de pestañas en el móvil",
    description:
      "Nuevo inicio y nueva agenda en el móvil, con una barra de pestañas flotante y un botón + que despliega el menú de acciones.",
    categories: ["new", "mobile"],
  },
  {
    pr: 2,
    mergedAt: "2026-11-01T18:01:56Z",
    title: "La app se recupera sola tras una actualización",
    description:
      "Cuando hay una versión nueva, la app avisa y se actualiza sin quedarse cargando ni recargarse una y otra vez.",
    categories: ["fix"],
  },
  {
    pr: 1,
    mergedAt: "2026-11-01T16:29:52Z",
    title: "Nuevo aspecto de Avante",
    description:
      "Avante estrena diseño: barra lateral en el ordenador, barra de pestañas en el móvil, tarjetas más limpias y un icono nuevo.",
    categories: ["new", "mobile", "desktop"],
  },
];

export interface ChangelogDay {
  /** Calendar day in `CHANGELOG_TIME_ZONE`, as YYYY-MM-DD. */
  day: string;
  entries: ChangelogEntry[];
}

const dayKey = new Intl.DateTimeFormat("en-CA", {
  timeZone: CHANGELOG_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** The Madrid calendar day (YYYY-MM-DD) of a UTC timestamp. */
export const changelogDay = (mergedAt: string): string =>
  dayKey.format(new Date(mergedAt));

/** Entries grouped by Madrid calendar day, newest day and entry first. */
export const groupChangelogByDay = (
  entries: readonly ChangelogEntry[],
): ChangelogDay[] => {
  const sorted = [...entries].sort((left, right) =>
    right.mergedAt.localeCompare(left.mergedAt),
  );
  return sorted.reduce<ChangelogDay[]>((days, entry) => {
    const day = changelogDay(entry.mergedAt);
    const last = days[days.length - 1];
    return last?.day === day
      ? [...days.slice(0, -1), { day, entries: [...last.entries, entry] }]
      : [...days, { day, entries: [entry] }];
  }, []);
};
